import { Dropdown } from "react-bootstrap";
import { memo, useEffect, useState, useRef } from "react";
import ConfirmModal from "../../ConfirmModal";

function NotesInput({ value, onSave }) {
  const [localValue, setLocalValue] = useState(value || "");
  const textareaRef = useRef(null);

  const resizeTextarea = () => {
    if (!textareaRef.current) return;

    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height =
      textareaRef.current.scrollHeight + "px";
  };

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  useEffect(() => {
    resizeTextarea();
  }, [localValue]);

  useEffect(() => {
    const timeout = setTimeout(resizeTextarea, 0);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .risk-notes-textarea {
            min-height: 120px !important;
            font-size: 16px !important;
          }
        }
      `}</style>

      <textarea
        ref={textareaRef}
        className="form-control form-control-sm risk-notes-textarea"
        rows={1}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={resizeTextarea}
        onBlur={() => onSave(localValue)}
        style={{
          fontFamily: "Lato, sans-serif",
          resize: "none",
          overflow: "hidden",
          minHeight: "34px",
          whiteSpace: "pre-wrap",
        }}
      />
    </>
  );
}

function RiskDropdown({ row, sIdx, rIdx, onRowChange }) {
  return (
    <Dropdown className="w-100">
      <Dropdown.Toggle
        size="sm"
        variant="outline-secondary"
        className="w-100 d-flex align-items-center justify-content-center"
      >
        {row.risk ? (
          <span
            style={{
              fontSize: "1.2rem",
              color:
                row.risk === "low"
                  ? "#04aa00"
                  : row.risk === "medium"
                  ? "#d0c407"
                  : "#ba3b46",
            }}
          >
            ✓
          </span>
        ) : (
          <span className="text-muted" style={{ fontSize: "0.9rem" }}>
            Valitse riski
          </span>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="w-100 text-center">
        {[
          ["low", "#04aa00", "Matala riski"],
          ["medium", "#d0c407", "Keskitason riski"],
          ["high", "#ba3b46", "Korkea riski"],
        ].map(([value, color, label]) => (
          <Dropdown.Item
            key={value}
            className="d-flex align-items-center justify-content-center"
            onClick={() => onRowChange(sIdx, rIdx, { risk: value })}
          >
            <span style={{ color, fontSize: "1.2rem", marginRight: "6px" }}>
              ✓
            </span>
            {label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

const RiskRow = memo(function RiskRow({
  row,
  sIdx,
  rIdx,
  editable,
  isEditing,
  tempLabel,
  setTempLabel,
  startEditLabel,
  saveLabel,
  cancelEdit,
  onRowChange,
  onDeleteRow,
}) {
  return (
    <tr>
      <td className="fw-semibold">
        {editable && isEditing ? (
          <div className="d-flex align-items-center gap-1">
            <input
              className="form-control form-control-sm"
              value={tempLabel}
              onChange={(e) => setTempLabel(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={() => saveLabel(sIdx, rIdx)}
            >
              ✓
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={cancelEdit}
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="d-flex align-items-center gap-1">
            <span className="text-truncate">{row.name}</span>
            {editable && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => startEditLabel(sIdx, rIdx, row.name)}
              >
                <i className="bi bi-pencil-square text-muted" />
              </button>
            )}
          </div>
        )}
      </td>

      <td className="text-center">
        <RiskDropdown
          row={row}
          sIdx={sIdx}
          rIdx={rIdx}
          onRowChange={onRowChange}
        />
      </td>

      <td className="text-center">
        <input
          type="checkbox"
          defaultChecked={!!row.extra}
          onChange={(e) =>
            onRowChange(sIdx, rIdx, { extra: e.target.checked })
          }
        />
      </td>

      <td>
        {editable ? (
          <NotesInput
            value={row.notes}
            onSave={(value) => onRowChange(sIdx, rIdx, { notes: value })}
          />
        ) : (
          row.notes
        )}
      </td>

      <td className="text-center">
        {editable && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onDeleteRow(sIdx, rIdx)}
          >
            ✕
          </button>
        )}
      </td>
    </tr>
  );
});

function RiskMobileCard({
  row,
  sIdx,
  rIdx,
  editable,
  isEditing,
  tempLabel,
  setTempLabel,
  startEditLabel,
  saveLabel,
  cancelEdit,
  onRowChange,
  onDeleteRow,
}) {
  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="mb-3">
        {editable && isEditing ? (
          <div className="d-flex gap-1">
            <input
              className="form-control form-control-sm"
              value={tempLabel}
              onChange={(e) => setTempLabel(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={() => saveLabel(sIdx, rIdx)}
            >
              ✓
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={cancelEdit}
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div className="fw-semibold">{row.name}</div>
            {editable && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => startEditLabel(sIdx, rIdx, row.name)}
              >
                <i className="bi bi-pencil-square text-muted" />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label small fw-bold">Riski</label>
        <RiskDropdown
          row={row}
          sIdx={sIdx}
          rIdx={rIdx}
          onRowChange={onRowChange}
        />
      </div>

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          defaultChecked={!!row.extra}
          id={`extra-${sIdx}-${rIdx}`}
          onChange={(e) =>
            onRowChange(sIdx, rIdx, { extra: e.target.checked })
          }
        />
        <label
          className="form-check-label fw-semibold"
          htmlFor={`extra-${sIdx}-${rIdx}`}
        >
          Lisäselvitys
        </label>
      </div>

      <div className="mb-3">
        <label className="form-label small fw-bold">Havaitut puutteet</label>
        <NotesInput
          value={row.notes}
          onSave={(value) => onRowChange(sIdx, rIdx, { notes: value })}
        />
      </div>

      {editable && (
        <div className="text-end">
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onDeleteRow(sIdx, rIdx)}
          >
            Poista
          </button>
        </div>
      )}
    </div>
  );
}

export function KiinteistoRiskReport({
  sections,
  onRowChange,
  onAddRow,
  onDeleteRow,
  editable = false,
}) {
  const [editingRow, setEditingRow] = useState(null);
  const [tempLabel, setTempLabel] = useState("");

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    sectionIndex: null,
    rowIndex: null,
  });

  const requestDeleteRow = (sectionIndex, rowIndex) => {
    setConfirmModal({
      show: true,
      sectionIndex,
      rowIndex,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      show: false,
      sectionIndex: null,
      rowIndex: null,
    });
  };

  const confirmDeleteRow = () => {
    if (
      confirmModal.sectionIndex !== null &&
      confirmModal.rowIndex !== null
    ) {
      onDeleteRow(confirmModal.sectionIndex, confirmModal.rowIndex);
    }

    closeConfirmModal();
  };

  const startEditLabel = (sIdx, rIdx, name) => {
    setEditingRow(`${sIdx}-${rIdx}`);
    setTempLabel(name);
  };

  const saveLabel = (sIdx, rIdx) => {
    onRowChange(sIdx, rIdx, { name: tempLabel });
    setEditingRow(null);
    setTempLabel("");
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setTempLabel("");
  };

  return (
    <div className="container-md mt-4">
      <style>{`
        @media (max-width: 768px) {
          .risk-desktop-table {
            display: none;
          }
          .risk-mobile-list {
            display: block;
          }
        }

        @media (min-width: 769px) {
          .risk-mobile-list {
            display: none;
          }
        }
      `}</style>

      {sections.map((section, sIdx) => (
        <div key={section.title} className="mb-4">
          <div className="fw-bold text-uppercase border-bottom pb-1 mb-2">
            {section.title}
          </div>

          <table className="table table-sm align-middle risk-desktop-table">
            <thead>
              <tr style={{ borderBottom: "2px solid #198754" }}>
                <th style={{ width: "30%" }}></th>
                <th style={{ width: "10%" }} className="text-center">
                  Riski
                </th>
                <th style={{ width: "15%" }} className="text-center">
                  Lisäselvitys
                </th>
                <th>Havaitut puutteet</th>
                <th style={{ width: "50px" }}></th>
              </tr>
            </thead>

            <tbody>
              {section.rows.map((row, rIdx) => (
                <RiskRow
                  key={`${section.title}-${rIdx}`}
                  row={row}
                  sIdx={sIdx}
                  rIdx={rIdx}
                  editable={editable}
                  isEditing={editingRow === `${sIdx}-${rIdx}`}
                  tempLabel={tempLabel}
                  setTempLabel={setTempLabel}
                  startEditLabel={startEditLabel}
                  saveLabel={saveLabel}
                  cancelEdit={cancelEdit}
                  onRowChange={onRowChange}
                  onDeleteRow={requestDeleteRow}
                />
              ))}
            </tbody>
          </table>

          <div className="risk-mobile-list">
            {section.rows.map((row, rIdx) => (
              <RiskMobileCard
                key={`${section.title}-mobile-${rIdx}`}
                row={row}
                sIdx={sIdx}
                rIdx={rIdx}
                editable={editable}
                isEditing={editingRow === `${sIdx}-${rIdx}`}
                tempLabel={tempLabel}
                setTempLabel={setTempLabel}
                startEditLabel={startEditLabel}
                saveLabel={saveLabel}
                cancelEdit={cancelEdit}
                onRowChange={onRowChange}
                onDeleteRow={requestDeleteRow}
              />
            ))}
          </div>

          {editable && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onAddRow(sIdx)}
            >
              + Lisää rivi
            </button>
          )}
        </div>
      ))}

      <ConfirmModal
        show={confirmModal.show}
        title="Poista rivi"
        message="Haluatko varmasti poistaa tämän rivin?"
        confirmText="Poista"
        cancelText="Peruuta"
        confirmVariant="danger"
        onClose={closeConfirmModal}
        onConfirm={confirmDeleteRow}
      />
    </div>
  );
}